export const COLS = 10;
export const ROWS = 20;

export type Kind = "I" | "O" | "T" | "S" | "Z" | "J" | "L";
export type Cell = Kind | null;
export type Board = Cell[][];
export type Matrix = number[][];
export interface Piece {
  kind: Kind;
  m: Matrix;
  x: number;
  y: number;
}
export type GameEvent =
  | "start"
  | "move"
  | "rotate"
  | "land"
  | "hard"
  | "clear1"
  | "clear2"
  | "clear3"
  | "clear4"
  | "over";
export type Status = "ready" | "playing" | "paused" | "over";

export interface State {
  board: Board;
  piece: Piece;
  next: Kind;
  bag: Kind[];
  score: number;
  lines: number;
  level: number;
  status: Status;
  event: GameEvent | null;
  seq: number; // bumps on every event so the UI can play a sound
}

export type Action =
  | { type: "start" }
  | { type: "left" }
  | { type: "right" }
  | { type: "down" }
  | { type: "rotate" }
  | { type: "hard" }
  | { type: "tick" }
  | { type: "pause" };

export const KINDS: Kind[] = ["I", "O", "T", "S", "Z", "J", "L"];

export const SHAPES: Record<Kind, Matrix> = {
  I: [
    [0, 0, 0, 0],
    [1, 1, 1, 1],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
  ],
  O: [
    [1, 1],
    [1, 1],
  ],
  T: [
    [0, 1, 0],
    [1, 1, 1],
    [0, 0, 0],
  ],
  S: [
    [0, 1, 1],
    [1, 1, 0],
    [0, 0, 0],
  ],
  Z: [
    [1, 1, 0],
    [0, 1, 1],
    [0, 0, 0],
  ],
  J: [
    [1, 0, 0],
    [1, 1, 1],
    [0, 0, 0],
  ],
  L: [
    [0, 0, 1],
    [1, 1, 1],
    [0, 0, 0],
  ],
};

const POINTS = [0, 100, 300, 500, 800];

const rotateCW = (m: Matrix): Matrix =>
  m[0].map((_, c) => m.map((row) => row[c]).reverse());

const emptyBoard = (): Board =>
  Array.from({ length: ROWS }, () => Array<Cell>(COLS).fill(null));

const shuffle = (a: Kind[]): Kind[] => {
  const b = [...a];
  for (let i = b.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [b[i], b[j]] = [b[j], b[i]];
  }
  return b;
};

const draw = (bag: Kind[]): [Kind, Kind[]] => {
  const [k, ...rest] = bag.length ? bag : shuffle(KINDS);
  return [k, rest];
};

const spawn = (kind: Kind): Piece => {
  const m = SHAPES[kind];
  return { kind, m, x: Math.floor((COLS - m[0].length) / 2), y: 0 };
};

export const collides = (board: Board, m: Matrix, px: number, py: number) =>
  m.some((row, r) =>
    row.some((v, c) => {
      if (!v) return false;
      const x = px + c,
        y = py + r;
      return (
        x < 0 || x >= COLS || y >= ROWS || (y >= 0 && board[y][x] !== null)
      );
    }),
  );

export const ghostY = (board: Board, p: Piece) => {
  let y = p.y;
  while (!collides(board, p.m, p.x, y + 1)) y++;
  return y;
};

export const levelInterval = (level: number) =>
  Math.max(90, 800 - (level - 1) * 70);

export function initialState(): State {
  const [k, bag] = draw([]);
  const [n, bag2] = draw(bag);
  return {
    board: emptyBoard(),
    piece: spawn(k),
    next: n,
    bag: bag2,
    score: 0,
    lines: 0,
    level: 1,
    status: "ready",
    event: null,
    seq: 0,
  };
}

const emit = (s: State, event: GameEvent): State => ({
  ...s,
  event,
  seq: s.seq + 1,
});

function lock(s: State, hard: boolean): State {
  const board = s.board.map((r) => [...r]);
  s.piece.m.forEach((row, r) =>
    row.forEach((v, c) => {
      const y = s.piece.y + r,
        x = s.piece.x + c;
      if (v && y >= 0) board[y][x] = s.piece.kind;
    }),
  );
  const kept = board.filter((row) => row.some((c) => c === null));
  const cleared = ROWS - kept.length;
  const nextBoard = [
    ...Array.from({ length: cleared }, () => Array<Cell>(COLS).fill(null)),
    ...kept,
  ];
  const lines = s.lines + cleared;
  const [nk, bag] = draw(s.bag);
  const piece = spawn(s.next);
  const over = collides(nextBoard, piece.m, piece.x, piece.y);
  const event: GameEvent = over
    ? "over"
    : cleared
      ? (`clear${cleared}` as GameEvent)
      : hard
        ? "hard"
        : "land";
  return {
    ...s,
    board: nextBoard,
    piece,
    next: nk,
    bag,
    lines,
    level: Math.floor(lines / 10) + 1,
    score: s.score + POINTS[cleared] * s.level,
    status: over ? "over" : "playing",
    event,
    seq: s.seq + 1,
  };
}

function step(s: State, soft: boolean): State {
  if (collides(s.board, s.piece.m, s.piece.x, s.piece.y + 1))
    return lock(s, false);
  return {
    ...s,
    piece: { ...s.piece, y: s.piece.y + 1 },
    score: s.score + (soft ? 1 : 0),
  };
}

export function reducer(s: State, a: Action): State {
  if (a.type === "start")
    return emit({ ...initialState(), status: "playing" }, "start");
  if (a.type === "pause") {
    if (s.status === "playing") return { ...s, status: "paused" };
    if (s.status === "paused") return { ...s, status: "playing" };
    return s;
  }
  if (s.status !== "playing") return s;

  const p = s.piece;
  switch (a.type) {
    case "tick":
      return step(s, false);
    case "down":
      return step(s, true);
    case "left":
    case "right": {
      const nx = p.x + (a.type === "left" ? -1 : 1);
      if (collides(s.board, p.m, nx, p.y)) return s;
      return emit({ ...s, piece: { ...p, x: nx } }, "move");
    }
    case "rotate": {
      const m = rotateCW(p.m);
      for (const dx of [0, -1, 1, -2, 2]) {
        // simple wall kicks
        if (!collides(s.board, m, p.x + dx, p.y)) {
          return emit({ ...s, piece: { ...p, m, x: p.x + dx } }, "rotate");
        }
      }
      return s;
    }
    case "hard": {
      const y = ghostY(s.board, p);
      return lock(
        { ...s, piece: { ...p, y }, score: s.score + 2 * (y - p.y) },
        true,
      );
    }
  }
  return s;
}
